import React, { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_CODES = [
  { flag: "🇮🇹", code: "+39", label: "IT" },
  { flag: "🇪🇸", code: "+34", label: "ES" },
  { flag: "🇬🇧", code: "+44", label: "GB" },
  { flag: "🇺🇸", code: "+1", label: "US" },
  { flag: "🇩🇪", code: "+49", label: "DE" },
  { flag: "🇫🇷", code: "+33", label: "FR" },
  { flag: "🇵🇹", code: "+351", label: "PT" },
  { flag: "🇧🇷", code: "+55", label: "BR" },
  { flag: "🇦🇷", code: "+54", label: "AR" },
  { flag: "🇲🇽", code: "+52", label: "MX" },
];

const inputStyle = {
  width: "100%",
  padding: "14px 18px",
  border: "1.5px solid rgba(255,255,255,0.6)",
  borderRadius: "10px",
  fontFamily: "'Poppins', sans-serif",
  fontSize: "15px",
  color: "#333",
  background: "rgba(255,255,255,0.75)",
  outline: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  boxSizing: "border-box",
};

const ContactSection = () => {
  const { t } = useLanguage();
  const c = t.contact;

  const [form, setForm] = useState({ name: "", prefix: "+39", phone: "", email: "", message: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [focused, setFocused] = useState("");

  const handle = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const focusStyle = (field) => ({
    ...inputStyle,
    borderColor: focused === field ? "#ffc84d" : "rgba(255,255,255,0.6)",
    boxShadow: focused === field ? "0 0 0 3px rgba(255,200,77,0.25)" : "none",
  });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: form.name,
        email: form.email,
        phone: `${form.prefix} ${form.phone}`,
        message: form.message,
      });
      setStatus("success");
      setForm({ name: "", prefix: "+39", phone: "", email: "", message: "", consent: false });
    } catch {
      setStatus("error");
    }
    setTimeout(() => setStatus("idle"), 5000);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      style={{
        position: "relative",
        padding: "100px 24px 100px",
        overflow: "hidden",
      }}
    >
      {/* Vivid gradient background */}
      <div style={{
        position: "absolute", inset: 0,
        background: `
          radial-gradient(ellipse 800px 600px at 10% 70%, rgba(255, 200, 77, 0.55) 0%, transparent 60%),
          radial-gradient(ellipse 700px 700px at 90% 20%, rgba(246, 81, 81, 0.45) 0%, transparent 60%),
          radial-gradient(ellipse 600px 500px at 55% 95%, rgba(81, 112, 255, 0.35) 0%, transparent 55%)
        `,
      }} />
      {/* Top fade from #fff (clients section) */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "80px",
        background: "linear-gradient(to bottom, #fff, transparent)",
        zIndex: 1,
      }} />

      <div style={{ position: "relative", zIndex: 2, maxWidth: "620px", margin: "0 auto" }}>
        <h2 style={{
          fontFamily: "'Archivo Black', 'Arial Black', sans-serif",
          fontWeight: 900,
          fontSize: "clamp(36px, 5vw, 60px)",
          color: "#333",
          textAlign: "center",
          margin: "0 0 12px 0",
          letterSpacing: "-1.5px",
        }}>
          {c.title}
        </h2>
        <div style={{
          width: "48px", height: "3px",
          background: "linear-gradient(90deg, #ffc84d, #f65151)",
          borderRadius: "2px", margin: "0 auto 48px",
        }} />

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <input data-testid="contact-name" type="text" placeholder={c.name} value={form.name}
            onChange={handle("name")} required
            style={focusStyle("name")}
            onFocus={() => setFocused("name")} onBlur={() => setFocused("")}
          />

          <div style={{ display: "flex", gap: "10px" }}>
            <select data-testid="contact-phone-prefix" value={form.prefix} onChange={handle("prefix")}
              style={{
                padding: "14px 10px",
                border: "1.5px solid rgba(255,255,255,0.6)",
                borderRadius: "10px",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "14px", color: "#333",
                background: "rgba(255,255,255,0.75)",
                cursor: "pointer", outline: "none",
                flexShrink: 0, width: "130px",
              }}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
              ))}
            </select>
            <input data-testid="contact-phone" type="tel" placeholder={c.phone} value={form.phone}
              onChange={handle("phone")} required
              style={{ ...focusStyle("phone"), flex: 1 }}
              onFocus={() => setFocused("phone")} onBlur={() => setFocused("")}
            />
          </div>

          <input data-testid="contact-email" type="email" placeholder={c.email} value={form.email}
            onChange={handle("email")} required
            style={focusStyle("email")}
            onFocus={() => setFocused("email")} onBlur={() => setFocused("")}
          />

          <textarea data-testid="contact-message" placeholder={c.message} value={form.message}
            onChange={handle("message")} rows={4}
            style={{ ...focusStyle("message"), resize: "vertical", minHeight: "120px" }}
            onFocus={() => setFocused("message")} onBlur={() => setFocused("")}
          />

          {/* Consent checkbox */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "4px 0" }}>
            <input
              data-testid="contact-consent"
              type="checkbox"
              id="consent-checkbox"
              checked={form.consent}
              onChange={(e) => setForm((prev) => ({ ...prev, consent: e.target.checked }))}
              style={{
                width: "18px", height: "18px",
                marginTop: "2px",
                accentColor: "#ffc84d",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            <label htmlFor="consent-checkbox" style={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: "13px",
              color: "#555",
              lineHeight: 1.55,
              cursor: "pointer",
            }}>
              {c.consent}{" "}
              <a
                href="https://www.iubenda.com/privacy-policy/55166439"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#5170ff", textDecoration: "underline" }}
              >
                {c.consentLink}
              </a>.
            </label>
          </div>

          {/* FULL WIDTH button, less curved */}
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={status === "sending" || !form.consent}
            style={{
              width: "100%",
              padding: "17px 32px",
              background: (status === "sending" || !form.consent)
                ? "rgba(180,180,180,0.8)"
                : "linear-gradient(135deg, #ffc84d 0%, #f65151 100%)",
              border: "none",
              borderRadius: "12px",
              fontFamily: "'Body Grotesque', 'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: "16px",
              color: "#fff",
              cursor: (status === "sending" || !form.consent) ? "not-allowed" : "pointer",
              letterSpacing: "0.05em",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              boxShadow: "0 6px 24px rgba(246,81,81,0.35)",
            }}
            onMouseEnter={(e) => {
              if (status !== "sending") {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 10px 32px rgba(246,81,81,0.45)";
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 6px 24px rgba(246,81,81,0.35)";
            }}
          >
            {status === "sending" ? c.sending : c.send}
          </button>

          {status === "success" && (
            <div data-testid="contact-success" style={{
              padding: "14px 20px",
              background: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(81,112,255,0.4)",
              borderRadius: "10px",
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px", color: "#5170ff",
            }}>
              {c.success}
            </div>
          )}
          {status === "error" && (
            <div data-testid="contact-error" style={{
              padding: "14px 20px",
              background: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(246,81,81,0.4)",
              borderRadius: "10px",
              fontFamily: "'Poppins', sans-serif",
              fontSize: "14px", color: "#f65151",
            }}>
              {c.error}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
